import { TextFieldStyled } from "@candidate/views/forms/applicationForm/AppellationInput/styles";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, InputAdornment, type SelectProps, type TextFieldProps, type Theme } from "@mui/material";
import type { TextFieldVariants } from "@mui/material";
import type { SxProps } from "@mui/material";
import { useMultiRefs } from "@root/utils/hooks/useMultiRef";
import { clsx } from "clsx";
import type { FieldPath, FieldPathValue, FieldValues, UseControllerReturn } from "react-hook-form";
import { TextFieldFormHelperText } from "./helperText";
import { textFieldStyledClasses } from "./styles";

export type TTextFieldFormExternalProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Variant extends TextFieldVariants,
> = Omit<TextFieldProps<Variant>, "name" | "value" | "onBlur" | "SelectProps"> & {
  SelectProps?: Partial<SelectProps<FieldPathValue<TFieldValues, TName>>>;
  clearable?: boolean;
  maxLength?: number;
  hideHelperText?: boolean;
  sx?: SxProps<Theme>;
} & UseControllerReturn<TFieldValues, TName>;

export const TextFieldFormExternal = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Variant extends TextFieldVariants,
>({
  field,
  fieldState,
  error,
  maxLength,
  clearable,
  helperText,
  hideHelperText,
  onChange,
  inputRef,
  ...props
}: TTextFieldFormExternalProps<TFieldValues, TName, Variant>) => {
  const selectProps: TTextFieldFormExternalProps<TFieldValues, TName, Variant>["SelectProps"] = {
    ...props.SelectProps,
    className: clsx(textFieldStyledClasses.select, props.SelectProps?.className),
  };
  const mutliRefs = useMultiRefs(field.ref, inputRef);
  return (
    <TextFieldStyled
      {...props}
      className={clsx(textFieldStyledClasses.root, props.className)}
      InputLabelProps={{
        ...props.InputLabelProps,
        ...(props.disabled && { shrink: true }),
      }}
      SelectProps={selectProps}
      name={field.name}
      value={field.value}
      onChange={(event) => {
        if (maxLength && event.target.value.length > maxLength) {
          event.target.value = event.target.value.slice(0, maxLength);
        }
        if (onChange) {
          onChange(event);
        }
        field.onChange(event);
      }}
      {...(clearable && {
        InputProps: {
          ...props.InputProps,
          endAdornment: field.value && (
            <InputAdornment className={textFieldStyledClasses.inputClearButton} position="end" sx={{ pr: 3 }}>
              <IconButton size="small" onClick={() => field.onChange("")}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      })}
      onBlur={field.onBlur}
      inputRef={mutliRefs}
      error={error ? true : Boolean(fieldState.error)}
      helperText={
        hideHelperText !== true
          ? (helperText ?? (
              <TextFieldFormHelperText
                helper={maxLength ? `${field.value?.length ?? 0}/${maxLength} caractères` : undefined}
                error={fieldState.error?.message}
              />
            ))
          : undefined
      }
    />
  );
};
